import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import {
  setReceiptList,
  setReceiptDetail
} from "../../store/slices/receiptSlice";
import {
  getReceiptByIdQuery,
  getReceiptByPaymentQuery,
  listReceiptsByInvoiceQuery,
  listReceiptsByOrderQuery,
  listReceiptsByStoreQuery,
  listReceiptsByCompanyQuery
} from "../../Graphql/querries/receiptQuery";

import {
  Receipt,
  GetReceiptResponse,
  ListReceiptsResponse,
  GetReceiptByIdInput,
  GetReceiptByPaymentInput,
  ListReceiptsByInvoiceInput,
  ListReceiptsByOrderInput
} from "../../OrderMFE/Receipt/types/receipt";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

// ─── Get Receipt ───────────────────────────────────────────────────────────────

/**
 * useGetReceiptById
 * Fetch a single receipt by its receiptId
 */
export const useGetReceiptById = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Receipt | null, Error, GetReceiptByIdInput>({
    mutationKey: ["getReceiptById"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching receipt by id",
        buildLogContext(
          "receipts",
          "get-receipt-by-id",
          userId,
          companyId,
          undefined,
          {
            receiptId: input.receiptId
          }
        )
      );

      try {
        const response: GetReceiptResponse = await fetchGraphQL(
          getReceiptByIdQuery,
          { input },
          setAlert
        );
        const { receipt, code } = response.getReceiptById;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch receipt by id",
            buildLogContext(
              "receipts",
              "get-receipt-by-id",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                receiptId: input.receiptId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!receipt) {
          captureAppException(
            "API returned OK but receipt object missing",
            new Error("API returned OK but receipt object missing"),
            buildLogContext(
              "receipts",
              "get-receipt-by-id",
              userId,
              companyId,
              undefined,
              {
                receiptId: input.receiptId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setReceiptDetail(receipt));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Receipt fetched by id successfully",
          buildLogContext(
            "receipts",
            "get-receipt-by-id",
            userId,
            companyId,
            undefined,
            {
              receiptId: receipt.receiptId
            }
          )
        );
        return receipt;
      } catch (error) {
        captureAppException(
          "Failed to fetch receipt by id",
          error,
          buildLogContext(
            "receipts",
            "get-receipt-by-id",
            userId,
            companyId,
            undefined,
            {
              receiptId: input.receiptId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

/**
 * useGetReceiptByPayment
 * Fetch the receipt generated for a specific payment
 */
export const useGetReceiptByPayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Receipt | null, Error, GetReceiptByPaymentInput>({
    mutationKey: ["getReceiptByPayment"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching receipt by payment",
        buildLogContext(
          "receipts",
          "get-receipt-by-payment",
          userId,
          companyId,
          undefined,
          {
            paymentId: input.paymentId
          }
        )
      );

      try {
        const response: GetReceiptResponse = await fetchGraphQL(
          getReceiptByPaymentQuery,
          { input },
          setAlert
        );
        const { receipt, code } = response.getReceiptByPayment;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch receipt by payment",
            buildLogContext(
              "receipts",
              "get-receipt-by-payment",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                paymentId: input.paymentId
              }
            )
          );
          // NOT_FOUND is expected when no receipt exists for this payment yet.
          if (code !== API_RESPONSE_CODE.NOT_FOUND) {
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: code as unknown as ALERT_BANNER_CODE
            });
          }
          return null;
        }

        if (!receipt) {
          captureAppException(
            "API returned OK but receipt object missing",
            new Error("API returned OK but receipt object missing"),
            buildLogContext(
              "receipts",
              "get-receipt-by-payment",
              userId,
              companyId,
              undefined,
              {
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setReceiptDetail(receipt));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Receipt fetched by payment successfully",
          buildLogContext(
            "receipts",
            "get-receipt-by-payment",
            userId,
            companyId,
            undefined,
            {
              receiptId: receipt.receiptId,
              paymentId: input.paymentId
            }
          )
        );
        return receipt;
      } catch (error) {
        captureAppException(
          "Failed to fetch receipt by payment",
          error,
          buildLogContext(
            "receipts",
            "get-receipt-by-payment",
            userId,
            companyId,
            undefined,
            {
              paymentId: input.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── List Receipts ─────────────────────────────────────────────────────────────

/**
 * useListReceiptsByInvoice
 * Fetch all receipts associated with a specific invoice
 */
export const useListReceiptsByInvoice = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Receipt[], Error, ListReceiptsByInvoiceInput>({
    mutationKey: ["listReceiptsByInvoice"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching receipts by invoice",
        buildLogContext(
          "receipts",
          "list-receipts-by-invoice",
          userId,
          companyId,
          undefined,
          {
            invoiceId: input.invoiceId
          }
        )
      );

      try {
        const response: ListReceiptsResponse = await fetchGraphQL(
          listReceiptsByInvoiceQuery,
          { input },
          setAlert
        );
        const { receipts, code } = response.listReceiptsByInvoice;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch receipts by invoice",
            buildLogContext(
              "receipts",
              "list-receipts-by-invoice",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!receipts) {
          captureAppException(
            "API returned OK but receipts array missing",
            new Error("API returned OK but receipts array missing"),
            buildLogContext(
              "receipts",
              "list-receipts-by-invoice",
              userId,
              companyId,
              undefined,
              {
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setReceiptList(receipts));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Receipts by invoice fetched successfully",
          buildLogContext(
            "receipts",
            "list-receipts-by-invoice",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId,
              receiptCount: receipts.length
            }
          )
        );
        return receipts;
      } catch (error) {
        captureAppException(
          "Failed to fetch receipts by invoice",
          error,
          buildLogContext(
            "receipts",
            "list-receipts-by-invoice",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

/**
 * useListReceiptsByOrder
 * Fetch all receipts associated with a specific order
 */
export const useListReceiptsByOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Receipt[], Error, ListReceiptsByOrderInput>({
    mutationKey: ["listReceiptsByOrder"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching receipts by order",
        buildLogContext(
          "receipts",
          "list-receipts-by-order",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId
          }
        )
      );

      try {
        const response: ListReceiptsResponse = await fetchGraphQL(
          listReceiptsByOrderQuery,
          { input },
          setAlert
        );
        const { receipts, code } = response.listReceiptsByOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch receipts by order",
            buildLogContext(
              "receipts",
              "list-receipts-by-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!receipts) {
          captureAppException(
            "API returned OK but receipts array missing",
            new Error("API returned OK but receipts array missing"),
            buildLogContext(
              "receipts",
              "list-receipts-by-order",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setReceiptList(receipts));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Receipts by order fetched successfully",
          buildLogContext(
            "receipts",
            "list-receipts-by-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId,
              receiptCount: receipts.length
            }
          )
        );
        return receipts;
      } catch (error) {
        captureAppException(
          "Failed to fetch receipts by order",
          error,
          buildLogContext(
            "receipts",
            "list-receipts-by-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

/**
 * useListReceiptsByStore
 * Fetch all receipts scoped to the current store
 */
export const useListReceiptsByStore = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Receipt[], Error, void>({
    mutationKey: ["listReceiptsByStore"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store receipts",
        buildLogContext(
          "receipts",
          "list-receipts-by-store",
          userId,
          companyId,
          storeId
        )
      );

      try {
        const response: ListReceiptsResponse = await fetchGraphQL(
          listReceiptsByStoreQuery,
          {},
          setAlert
        );
        const { receipts, code } = response.listReceiptsByStore;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch store receipts",
            buildLogContext(
              "receipts",
              "list-receipts-by-store",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!receipts) {
          captureAppException(
            "API returned OK but store receipts array missing",
            new Error("API returned OK but store receipts array missing"),
            buildLogContext(
              "receipts",
              "list-receipts-by-store",
              userId,
              companyId,
              storeId
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setReceiptList(receipts));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Store receipts fetched successfully",
          buildLogContext(
            "receipts",
            "list-receipts-by-store",
            userId,
            companyId,
            storeId,
            {
              receiptCount: receipts.length
            }
          )
        );
        return receipts;
      } catch (error) {
        captureAppException(
          "Failed to fetch store receipts",
          error,
          buildLogContext(
            "receipts",
            "list-receipts-by-store",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

/**
 * useListReceiptsByCompany
 * Fetch all receipts across the entire company
 */
export const useListReceiptsByCompany = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Receipt[], Error, void>({
    mutationKey: ["listReceiptsByCompany"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company receipts",
        buildLogContext(
          "receipts",
          "list-receipts-by-company",
          userId,
          companyId,
          undefined
        )
      );

      try {
        const response: ListReceiptsResponse = await fetchGraphQL(
          listReceiptsByCompanyQuery,
          {},
          setAlert
        );
        const { receipts, code } = response.listReceiptsByCompany;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch company receipts",
            buildLogContext(
              "receipts",
              "list-receipts-by-company",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!receipts) {
          captureAppException(
            "API returned OK but company receipts array missing",
            new Error("API returned OK but company receipts array missing"),
            buildLogContext(
              "receipts",
              "list-receipts-by-company",
              userId,
              companyId,
              undefined
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setReceiptList(receipts));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Company receipts fetched successfully",
          buildLogContext(
            "receipts",
            "list-receipts-by-company",
            userId,
            companyId,
            undefined,
            {
              receiptCount: receipts.length
            }
          )
        );
        return receipts;
      } catch (error) {
        captureAppException(
          "Failed to fetch company receipts",
          error,
          buildLogContext(
            "receipts",
            "list-receipts-by-company",
            userId,
            companyId,
            undefined
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};
